import { useState } from 'react'
import { Box, Button, Checkbox, HStack, Input, Text, VStack, chakra } from '@chakra-ui/react'
import { ResultStatus, VisitActionFlag } from '../../../types/script'
import { FOLLOW_UPS_FOR, defaultFollowUps, type EndChoice, type FollowUps } from '../../../lib/doorDraft'

const CHOICES: { value: EndChoice; label: string; result: ResultStatus }[] = [
  { value: 'finished', label: 'Finished', result: ResultStatus.Completed },
  { value: 'declined', label: 'They declined partway', result: ResultStatus.Refused },
  { value: 'busy', label: 'Busy, come back', result: ResultStatus.Busy },
  { value: 'language', label: 'Language barrier', result: ResultStatus.LanguageBarrier },
]

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <Checkbox.Root checked={checked} onCheckedChange={(e) => onChange(!!e.checked)} minH="44px">
      <Checkbox.HiddenInput />
      <Checkbox.Control />
      <Checkbox.Label>{label}</Checkbox.Label>
    </Checkbox.Root>
  )
}

/** [Spec §4.8] How did it end? Answers so far are always kept; this screen IS the confirmation. */
export function EndStep({ names, allowed, allowedFlags, preset, notice, rememberedLeftLiterature, onConfirm, isSaving, onBack }: {
  names: string; allowed: ReadonlySet<ResultStatus>
  /** [P6] The follow-ups the organizer left on. Omitted: all of them. */
  allowedFlags?: ReadonlySet<VisitActionFlag>
  preset: EndChoice | null; notice: string | null
  rememberedLeftLiterature: boolean; onConfirm: (choice: EndChoice, f: FollowUps) => void; isSaving: boolean
  /** [P5-3] Shown when this sheet was reached from a conversation. */
  onBack?: () => void
}) {
  const offered = CHOICES.filter(c => allowed.has(c.result))
  // [P5-3] No preset (End conversation): nothing selected until the canvasser picks. A preset the script
  // doesn't allow falls back to the first OFFERED choice, never to a hidden one.
  const initial: EndChoice | null = preset === null ? null
    : offered.some(c => c.value === preset) ? preset : (offered[0]?.value ?? preset)
  const [choice, setChoice] = useState<EndChoice | null>(initial)
  const [f, setF] = useState<FollowUps>({ ...defaultFollowUps(initial ?? 'finished'), leftLiterature: rememberedLeftLiterature })
  const [askDnc, setAskDnc] = useState(false)
  const flagOk = (k: keyof FollowUps) => {
    const flag = { leftLiterature: VisitActionFlag.LeftLiterature, comeBackLater: VisitActionFlag.ComeBackLater,
      scheduleCallback: VisitActionFlag.ScheduleCallback }[k as 'leftLiterature' | 'comeBackLater' | 'scheduleCallback']
    return !flag || !allowedFlags || allowedFlags.has(flag)
  }
  const shows = new Set(choice ? FOLLOW_UPS_FOR[choice].filter(flagOk) : [])
  const pick = (c: EndChoice) => {
    setChoice(c)
    setF(prev => ({ ...prev, comeBackLater: defaultFollowUps(c).comeBackLater, dnc: false }))
    setAskDnc(false)
  }

  return (
    <VStack flex={1} align="stretch" gap={4} px={4} pb={6} overflowY="auto">
      {onBack && <Button variant="ghost" minH="44px" alignSelf="flex-start" onClick={onBack}>Back to the conversation</Button>}
      <Text fontSize="xl" fontWeight="bold">How did it end?</Text>
      <Text color="fg.muted">{names}</Text>
      {notice && <Box p={3} borderRadius="md" bg="warning.subtle" color="warning.fg">{notice}</Box>}
      <VStack align="stretch" gap={2} role="radiogroup" aria-label="How did it end?">
        {offered.map(c => (
          <chakra.label key={c.value} display="flex" alignItems="center" gap={3} minH="52px" px={3} borderWidth="1px"
            borderRadius="md" borderColor={choice === c.value ? 'accent' : 'border'} bg={choice === c.value ? 'accent.soft' : 'surface'}>
            <input type="radio" name="end-choice" checked={choice === c.value} onChange={() => pick(c.value)} />
            <Text>{c.label}</Text>
          </chakra.label>
        ))}
      </VStack>
      <VStack align="stretch" gap={1}>
        {shows.has('leftLiterature') && <Check label="Left literature" checked={f.leftLiterature} onChange={v => setF({ ...f, leftLiterature: v })} />}
        {shows.has('comeBackLater') && <Check label="Come back later" checked={f.comeBackLater} onChange={v => setF({ ...f, comeBackLater: v })} />}
        {shows.has('scheduleCallback') && <Check label="Schedule callback" checked={f.scheduleCallback} onChange={v => setF({ ...f, scheduleCallback: v })} />}
        {shows.has('language') && (
          <Input aria-label="Language spoken" placeholder="Language spoken" minH="44px" maxLength={100}
            value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} />
        )}
        {shows.has('dnc') && (
          <Check label="Asked not to be contacted again?" checked={f.dnc || askDnc}
            onChange={v => { if (v) setAskDnc(true); else { setAskDnc(false); setF({ ...f, dnc: false }) } }} />
        )}
        {askDnc && !f.dnc && (
          <Box p={3} borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle">
            <Text mb={2}>They won't be contacted again by your organization.</Text>
            <HStack gap={2}>
              <Button minH="44px" bg="accent" color="accent.fg" onClick={() => setF({ ...f, dnc: true })}>Yes, don't contact</Button>
              <Button minH="44px" variant="outline" borderColor="border" onClick={() => setAskDnc(false)}>Cancel</Button>
            </HStack>
          </Box>
        )}
      </VStack>
      <Button minH="52px" bg="accent" color="accent.fg" disabled={isSaving || !choice}
        onClick={() => { if (choice) onConfirm(choice, { ...f, dnc: f.dnc && shows.has('dnc') }) }}>Done</Button>
    </VStack>
  )
}
